/**
 * Is this browser looking at the demo sandbox? (2026-07-28, all-in-one conversion)
 *
 * The sandbox used to be its own BUILD, keyed on NEXT_PUBLIC_DEMO_MODE inlined at
 * compile time. All-in-one means one build serves both app.insuracentral.com and
 * demo.insuracentral.com, so the frontend's demo verdict moves from build time to
 * RUNTIME, keyed on the hostname.
 *
 * Split of responsibilities, deliberately:
 *  - FRONTEND host check = presentation. Which Device is created, whether the trial
 *    prompt mounts, whether /auth redirects to /demo. Spoofing your own hostname only
 *    changes your own UI.
 *  - BACKEND actor check (demo_seats membership, resolved per request) = enforcement.
 *    Exports, spending, uploads are blocked server-side no matter what the browser
 *    claims to be.
 *
 * The legacy env flag still forces demo everywhere — that is what keeps local
 * development (`NEXT_PUBLIC_DEMO_MODE=true` in .env.local) working unchanged, and what
 * a dedicated single-purpose demo build would still use.
 *
 * This is a FUNCTION, not a const: a module-level const would be evaluated once at
 * import in an environment (SSR, prerender) where the hostname may not exist yet.
 * Every consumer calls it at decision time.
 */

const DEMO_HOSTNAME = 'demo.insuracentral.com';

export function isDemoHostname(host: string | null | undefined): boolean {
  if (!host) return false;
  const bare = host.split(':')[0].toLowerCase();
  if (bare === DEMO_HOSTNAME) return true;
  // Extra hosts for previews/local testing, e.g. NEXT_PUBLIC_DEMO_HOSTS=localhost,
  // demo-preview.onrender.com. Comma-separated, compared without port.
  const extra = (process.env.NEXT_PUBLIC_DEMO_HOSTS || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return extra.includes(bare);
}

export function isDemoMode(): boolean {
  // Legacy build-time force — dev workflow and dedicated demo builds.
  if (process.env.NEXT_PUBLIC_DEMO_MODE === 'true') return true;
  // Server components can't see the browser hostname from here; they pass the host
  // from headers() into isDemoHostname directly instead.
  if (typeof window === 'undefined') return false;
  return isDemoHostname(window.location.hostname);
}
